import { useEffect, useRef, useState } from 'react';
import { API_BASE } from '../constants/api';

export function getTbWsUrl(apiBase, token) {
  return apiBase.replace(/^http/, 'ws') + '/ws/plugins/telemetry?token=' + token;
}

const INITIAL_BACKOFF = 1000;
const MAX_BACKOFF = 30000;
const TELEMETRY_CMD_ID = 1;
const ATTRIBUTES_CMD_ID = 2;

export function useTbLiveData({ deviceId, token, enabled }) {
  const [liveTelemetry, setLiveTelemetry] = useState({});
  const [liveActive, setLiveActive] = useState(null);
  const [wsStatus, setWsStatus] = useState('closed');

  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const backoffRef = useRef(INITIAL_BACKOFF);
  const closedByUsRef = useRef(false);

  useEffect(() => {

    setLiveTelemetry({});
    setLiveActive(null);

    if (!enabled || !deviceId || !token) return undefined;

    closedByUsRef.current = false;
    backoffRef.current = INITIAL_BACKOFF;

    function connect() {
      setWsStatus('connecting');
      const ws = new WebSocket(getTbWsUrl(API_BASE, token));
      wsRef.current = ws;

      ws.onopen = () => {
        setWsStatus('open');
        backoffRef.current = INITIAL_BACKOFF;
        ws.send(JSON.stringify({
          tsSubCmds: [
            { entityType: 'DEVICE', entityId: deviceId, scope: 'LATEST_TELEMETRY', cmdId: TELEMETRY_CMD_ID },
          ],
          attrSubCmds: [
            { entityType: 'DEVICE', entityId: deviceId, scope: 'SERVER_SCOPE', keys: 'active', cmdId: ATTRIBUTES_CMD_ID },
          ],
          historyCmds: [],
        }));
      };

      ws.onmessage = (event) => {
        let msg;
        try { msg = JSON.parse(event.data); } catch { return; }
        if (!msg || !msg.data) return;

        // Attribut-Subscription (Aktiv-Status)
        if (msg.subscriptionId === ATTRIBUTES_CMD_ID) {
          const series = msg.data.active;
          if (Array.isArray(series) && series.length > 0) {
            const [, value] = series[series.length - 1];
            setLiveActive(value === 'true' || value === true);
          }
          return;
        }

        // Telemetrie-Subscription: neuesten Wert pro Schlüssel übernehmen
        setLiveTelemetry((prev) => {
          const next = { ...prev };
          for (const [key, series] of Object.entries(msg.data)) {
            if (Array.isArray(series) && series.length > 0) {
              const [ts, value] = series[series.length - 1];
              next[key] = { ts, value };
            }
          }
          return next;
        });
      };

      ws.onerror = () => {
        setWsStatus('error');
      };

      ws.onclose = () => {
        if (wsRef.current !== ws) return; 
        wsRef.current = null;
        if (closedByUsRef.current) {
          setWsStatus('closed');
          return;
        }
        setWsStatus('closed');
        const delay = backoffRef.current;
        reconnectTimerRef.current = setTimeout(() => {
          backoffRef.current = Math.min(backoffRef.current * 2, MAX_BACKOFF);
          connect();
        }, delay);
      };
    }

    connect();

    return () => {
      closedByUsRef.current = true;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [deviceId, token, enabled]);

  return { liveTelemetry, liveActive, wsStatus };
}
